import {initializeApp} from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import "firebase/storage";
import {
    getAuth,
    GoogleAuthProvider,
    signInWithPopup,
    signOut,
} from "firebase/auth";
import {
    collection,
    doc,
    getDocs,
    initializeFirestore,
    query,
    setDoc,
    where
} from "firebase/firestore";
import {getStorage, ref, deleteObject} from "firebase/storage";
import {DEFAULT_ADMIN_USERS} from "./firebase.constant";
import * as _ from "lodash";

const firebaseConfig = {
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_APP_ID,
    measurementId: process.env.REACT_APP_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);

const auth = getAuth(app);
const fireStoreDB = initializeFirestore(app, {ignoreUndefinedProperties: true});
// const fireStoreDB = getFirestore(app);
const firebaseStorage = getStorage(app);
const storageRef = ref;

const USERS_COLLECTION = "users";

const googleProvider = new GoogleAuthProvider();

const signInWithGoogle = async () => {
    try {
        const res = await signInWithPopup(auth, googleProvider);
        const user: any = res.user;

        let userInfo: any = {
            id: user.uid,
            uid: user.uid,
            isEnabled: false,
            isAdmin: false,
            displayName: user.displayName,
            authProvider: "google",
            email: user.email,
            emailVerified: user.reloadUserInfo.emailVerified,
            photoUrl: user.reloadUserInfo.photoUrl,
            lastLoginAt: user.reloadUserInfo.lastLoginAt,
            lastRefreshAt: user.reloadUserInfo.lastRefreshAt,
            createdAt: user.reloadUserInfo.createdAt,
        }

        const userQuery = query(collection(fireStoreDB, USERS_COLLECTION), where("email", "==", user.email));
        const userDocs = await getDocs(userQuery);

        if (userDocs.docs.length > 0) {
            const existingUserInfo = userDocs.docs[0].data();
            userInfo.isEnabled = existingUserInfo.isEnabled || false;
            userInfo.isAdmin = existingUserInfo.isAdmin || false;
        }

        const defaultAdminUsersByEmailMap = _.keyBy(DEFAULT_ADMIN_USERS, "email");
        const defaultAdminUser = defaultAdminUsersByEmailMap[user.email];

        if (defaultAdminUser) {
            userInfo.isEnabled = defaultAdminUser.isEnabled;
            userInfo.isAdmin = defaultAdminUser.isAdmin;
        }

        const userDocumentReference = doc(fireStoreDB, USERS_COLLECTION, user.uid);
        await setDoc(userDocumentReference, userInfo, {merge: true});

    } catch (err) {
        console.error(err);
    }
};

const logout = () => {
    signOut(auth);
};

const getCurrentUserIdToken = async () => {
    const user = auth.currentUser;
    const idToken = await user?.getIdToken();
    return idToken;
}

const appConfig = {
    appName: process.env.REACT_APP_NAME,
    appKey: process.env.REACT_APP_PROJECT_ID,
    appSocialName: process.env.REACT_APP_SOCIAL_NAME,
    appSocialShareText: process.env.REACT_APP_SOCIAL_SHARE_TEXT,
}

const cloudFunctionConfig = {
    baseUrl: process.env.REACT_APP_CLOUD_FUNCTION_BASE_URL,
    localBaseUrl: process.env.REACT_APP_CLOUD_FUNCTION_LOCAL_BASE_URL,
    syncShopifyProductsUrl: process.env.REACT_APP_SYNC_SHOPIFY_PRODUCTS_URL,
    getShopifyProductsSyncStatusUrl: process.env.REACT_APP_GET_SHOPIFY_PRODUCTS_SYNC_STATUS_URL,
    initiateInstagramPostScheduleUrl: process.env.REACT_APP_INITIATE_INSTAGRAM_POST_SCHEDULE_URL,
    initiateShopifyBulkProductUpdatesUrl: process.env.REACT_APP_INITIATE_SHOPIFY_BULK_PRODUCT_UPDATES_URL,
    getShopifyProductUpdatesStatusUrl: process.env.REACT_APP_GET_SHOPIFY_PRODUCT_UPDATES_STATUS_URL,
    syncShopifyBlogsListUrl: process.env.REACT_APP_SHOPIFY_SYNC_BLOGS_URL,
    syncShopifyCollectionsListUrl: process.env.REACT_APP_SHOPIFY_SYNC_COLLECTIONS_URL,
    updateShopifyCollectionsDetailsUrl: process.env.REACT_APP_SHOPIFY_UPDATE_COLLECTIONS_DETAILS_URL,
    initPublishArticleToShopifyBlogUrl: process.env.REACT_APP_SHOPIFY_PUBLISH_ARTICLE_TO_SHOPIFY_BLOG_URL,
    syncPrintfulCategoriesUrl: process.env.REACT_APP_SYNC_PRINTFUL_CATEGORIES_URL,
    syncPrintfulProductTypesUrl: process.env.REACT_APP_SYNC_PRINTFUL_PRODUCT_TYPES_URL,
    syncPrintfulProductsUrl: process.env.REACT_APP_SYNC_PRINTFUL_PRODUCTS_URL,
    syncPrintfulStoresUrl: process.env.REACT_APP_SYNC_PRINTFUL_STORES_URL,
    syncPrintfulSyncProductsUrl: process.env.REACT_APP_SYNC_PRINTFUL_SYNC_PRODUCTS_URL,
    initiateShopifyProductPriceSyncUrl: process.env.REACT_APP_INITIATE_SHOPIFY_PRODUCT_PRICES_SYNC_STATUS_URL,
    getShopifyProductPricesSyncStatusUrl: process.env.REACT_APP_GET_SHOPIFY_PRODUCT_PRICES_SYNC_STATUS_URL,
    bulkUpdateDesignContentUrl: process.env.REACT_APP_BULK_UPDATE_DESIGN_CONTENT_URL,
    getImageDownloadUrl: process.env.REACT_APP_BULK_DOWNLOAD_IMAGE_URL,
    uploadShopifyCollectionImageUrl: process.env.REACT_APP_SHOPIFY_COLLECTION_IMAGE_UPLOAD_URL,
}

const openAIConfig = {
    apiKey: process.env.REACT_APP_OPEN_AI_API_KEY
}

const facebookConfig = {
    adAccountId: process.env.REACT_APP_FACEBOOK_AD_ACCOUNT_ID,
    pageId: process.env.REACT_APP_FACEBOOK_PAGE_ID,
    instagramActorId: process.env.REACT_APP_INSTAGRAM_ACTOR_ID,
    accessToken: process.env.REACT_APP_FACEBOOK_ACCESS_TOKEN,
    graphApiBaseUri: process.env.REACT_APP_FACEBOOK_GRAPH_API_BASE_URI,
}

const shopifyConfig = {
    shopApiGraphqlUrl: process.env.REACT_APP_SHOPIFY_API_GRAPHQL_URL,
    shopApiAccessToken: process.env.REACT_APP_SHOPIFY_API_ACCESS_TOKEN,
}

const pinterestConfig = {
    appId: process.env.REACT_APP_PINTEREST_APP_ID,
    appSecretKey: process.env.REACT_APP_PINTEREST_APP_SECRET,
    oauthUrl: process.env.REACT_APP_PINTEREST_APP_OAUTH_URL,
    accessTokenUrl: process.env.REACT_APP_PINTEREST_APP_ACCESS_TOKEN_URL,
    redirectUrl: process.env.REACT_APP_PINTEREST_APP_REDIRECT_URL,
    accessToken: process.env.REACT_APP_PINTEREST_APP_SANDBOX_ACCESS_TOKEN,
    proxyApi: process.env.REACT_APP_PINTEREST_PROXY_API,
}

export {
    cloudFunctionConfig,
    openAIConfig,
    facebookConfig,
    pinterestConfig,
    shopifyConfig,
    appConfig,
    auth,
    fireStoreDB,
    firebaseStorage,
    storageRef,
    deleteObject,
    signInWithGoogle,
    logout,
    getCurrentUserIdToken,
};
